import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * The APK is a static file in `public/`, so CRA copies it into the build
 * untouched and nginx serves it straight off disk. No API call, no login and no
 * gate: anyone who lands on the site can download it.
 */
export const APK_URL = `${process.env.PUBLIC_URL || ''}/assets/apk/app-release.apk`;

/** Written at build time by scripts/apk-info.mjs. */
export const APK_INFO_URL = `${process.env.PUBLIC_URL || ''}/assets/apk/apk-info.json`;

/** What the file is called once it reaches the phone. */
export const APK_FILENAME = 'VIPKalyan.apk';

const DownloadContext = createContext({
  status: 'idle',
  received: 0,
  total: 0,
  error: '',
  start: () => {},
  close: () => {},
});

const formatMB = (bytes) => `${(bytes / 1048576).toFixed(1)} MB`;

export function DownloadProvider({ children }) {
  // idle -> downloading -> done, or -> error
  const [status, setStatus] = useState('idle');
  const [received, setReceived] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  const abortRef = useRef(null);
  const objectUrlRef = useRef('');
  const aliveRef = useRef(true);

  useEffect(
    () => () => {
      aliveRef.current = false;
      if (abortRef.current) abortRef.current.abort();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    []
  );

  /** Hands the finished bytes to the browser's download manager. */
  const save = useCallback((blob) => {
    const url = URL.createObjectURL(blob);
    objectUrlRef.current = url;

    const a = document.createElement('a');
    a.href = url;
    a.download = APK_FILENAME;
    document.body.appendChild(a);
    a.click();
    a.remove();

    // Revoking immediately can cancel the save on some browsers, so the URL is
    // released a moment later instead.
    window.setTimeout(() => {
      URL.revokeObjectURL(url);
      if (objectUrlRef.current === url) objectUrlRef.current = '';
    }, 60000);
  }, []);

  /**
   * Falls back to letting the browser fetch the file itself. Used whenever the
   * streaming read cannot run — an old browser, a blocked ReadableStream, a
   * device that will not hold 38 MB in memory. The download still happens, just
   * without a progress bar.
   */
  const plainDownload = useCallback(() => {
    const a = document.createElement('a');
    a.href = APK_URL;
    a.download = APK_FILENAME;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  const start = useCallback(async () => {
    if (status === 'downloading') {
      return;
    }

    setStatus('downloading');
    setReceived(0);
    setTotal(0);
    setError('');

    // The size cannot come from the APK response: it is served gzipped and
    // chunked, so there is no Content-Length to read. This descriptor is
    // generated from the real file at build time.
    let expected = 0;
    try {
      const infoRes = await fetch(APK_INFO_URL, { cache: 'no-store' });
      if (infoRes.ok) {
        const info = await infoRes.json();
        expected = Number(info?.bytes) || 0;
      }
    } catch (err) {
      // Not fatal — the bar just shows megabytes instead of a percentage.
    }

    if (!aliveRef.current) return;
    setTotal(expected);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(APK_URL, { signal: controller.signal });

      if (!res.ok) {
        throw new Error(`Server responded ${res.status}`);
      }

      if (!res.body || !res.body.getReader) {
        throw new Error('no-stream');
      }

      const reader = res.body.getReader();
      const chunks = [];
      let got = 0;

      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        got += value.length;
        if (aliveRef.current) setReceived(got);
      }

      if (!aliveRef.current) return;

      save(new Blob(chunks, { type: 'application/vnd.android.package-archive' }));
      setReceived(got);
      setStatus('done');
    } catch (err) {
      if (!aliveRef.current || err?.name === 'AbortError') {
        return;
      }

      // Anything at all went wrong: hand the job to the browser rather than
      // leaving the visitor with no APK.
      plainDownload();
      setStatus('done');
      setError(
        err?.message === 'no-stream'
          ? ''
          : 'Progress could not be tracked, so the download was handed to your browser.'
      );
    } finally {
      abortRef.current = null;
    }
  }, [status, save, plainDownload]);

  const close = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStatus('idle');
    setReceived(0);
    setTotal(0);
    setError('');
  }, []);

  const value = useMemo(
    () => ({ status, received, total, error, start, close }),
    [status, received, total, error, start, close]
  );

  return <DownloadContext.Provider value={value}>{children}</DownloadContext.Provider>;
}

export const useDownload = () => useContext(DownloadContext);

/**
 * Every download control on the site renders through this, so the hero button,
 * the drawer item and the fixed bottom bar cannot drift apart — one URL, one
 * progress dialog, one set of install instructions.
 *
 * It stays a real anchor to the real file: that keeps middle-click, long-press
 * "save link" and a JavaScript failure all working, while the click handler
 * upgrades the normal case to a tracked download.
 */
export function DownloadLink({ className, style, children, onClick }) {
  const { start } = useDownload();

  return (
    <a
      className={className}
      style={style}
      href={APK_URL}
      download={APK_FILENAME}
      onClick={(e) => {
        if (onClick) onClick(e);
        // Let modified clicks (new tab, save as) behave normally.
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
          return;
        }
        e.preventDefault();
        start();
      }}
    >
      {children}
    </a>
  );
}

/** The progress dialog and, once the file has landed, how to install it. */
export function DownloadModal() {
  const { status, received, total, error, close } = useDownload();

  if (status === 'idle') {
    return null;
  }

  const done = status === 'done';
  const pct = total > 0 ? Math.min(100, Math.round((received / total) * 100)) : 0;

  return (
    <div className="dlmodal" role="dialog" aria-modal="true" aria-label="Download app">
      <div className="dlmodal-card">
        <button type="button" className="dlmodal-x" onClick={close} aria-label="Close">
          ×
        </button>

        <h3>{done ? 'Download complete' : 'Downloading VIP Kalyan'}</h3>

        <div className="dlbar-track">
          <div
            className={`dlbar-fill ${!done && total === 0 ? 'indet' : ''}`}
            style={{ width: done ? '100%' : total > 0 ? `${pct}%` : '100%' }}
          />
        </div>

        <p className="dlmodal-num">
          {done
            ? `${formatMB(received || total)} downloaded`
            : total > 0
            ? `${formatMB(received)} of ${formatMB(total)} · ${pct}%`
            : `${formatMB(received)} downloaded`}
        </p>

        {done ? (
          <>
            <ol className="dlsteps">
              <li>
                Open <b>{APK_FILENAME}</b> from your notifications, or from your{' '}
                <b>Downloads</b> folder.
              </li>
              <li>
                Android will warn about installing from an unknown source. Tap{' '}
                <b>Settings</b> and turn on <b>Allow from this source</b>, then go back.
              </li>
              <li>
                Tap <b>Install</b>, then <b>Open</b> to start playing.
              </li>
            </ol>
            {error ? <p className="dlnote">{error}</p> : null}
            <p className="dlnote">
              Nothing saved?{' '}
              <a href={APK_URL} download={APK_FILENAME}>
                Download again
              </a>
              .
            </p>
            <button type="button" className="btn" onClick={close}>
              Done
            </button>
          </>
        ) : (
          <p className="dlnote">Keep this page open until the download finishes.</p>
        )}
      </div>
    </div>
  );
}
